/**
 * Assistant markdown → HTML for chat bubbles.
 *
 * The output of `renderMarkdown` goes through `dangerouslySetInnerHTML`
 * (ChatWidget.tsx), on a page this widget does not own — a small business's
 * website, reached by a pasted script tag, typically with no Content-Security
 * Policy of its own. There is no second line of defence out there. This module
 * is the whole of it, which is why it lives on its own with tests
 * (scripts/test-markdown.mjs) instead of inline in the component.
 *
 * 🚨 THE RULE: every value interpolated into the HTML below is either a
 * literal in this file or has passed through `escapeHtml`, `safeHref` or
 * `safeColor` first. Never add a `${…}` that is none of those.
 *
 * What went wrong before (shared-chatbot#288): the escaper covered `&`, `<`
 * and `>` but NOT the double quote, while the link rule interpolated the
 * captured URL straight into `href="$2"`, and its URL pattern `[^)\s]+`
 * admits a quote. Assistant text of the form
 *
 *   [click](https://example.com"onmouseover="…)
 *
 * therefore closed `href` early and appended an event-handler attribute —
 * browsers accept `<a href="x"onmouseover="y">` with no space between them, so
 * the pattern's no-whitespace rule was not the barrier it looked like. The
 * docstring claimed "Escapes first, so the output is XSS-safe"; it was not.
 *
 * Assistant text is not trusted input. It is model output derived from the
 * client's own crawled pages, and those routinely carry third-party text —
 * reviews, comments, embedded feeds — which is a published route for indirect
 * prompt injection. `prompt_guard` narrows that; it is not a parser and cannot
 * be the reason this function is safe.
 */

/** The widget's default accent, mirrored from ChatWidget's prop default. */
const DEFAULT_BRAND_COLOR = '#2563eb';

/**
 * HTML metacharacters — INCLUDING BOTH QUOTES.
 *
 * The quotes are the point. Escaped text is interpolated into attribute
 * values as well as element content, so an unescaped `"` is an attribute
 * break-out and an unescaped `'` is the same wherever a single-quoted
 * attribute is used. Escaping them here means no later rule in this file has
 * to think about it: by the time any `${…}` runs, a quote is already `&quot;`.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * A link target we are willing to emit, or null.
 *
 * Runs on text that has ALREADY been escaped, so the few entities the escaper
 * produces are decoded before parsing and the result is re-escaped after — a
 * URL is not trusted just because it parsed.
 *
 * `new URL` rather than a regex: it settles the scheme question exactly, and
 * scheme is what matters. Only http and https are emitted — never `javascript:`,
 * never `data:`. The caller's pattern already demands an `https?://` prefix;
 * this is the check that does not depend on that pattern staying right.
 */
export function safeHref(escapedUrl: string): string | null {
  const raw = escapedUrl
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  return escapeHtml(parsed.href);
}

/**
 * A colour we are willing to put in a `style` attribute, or the default.
 *
 * `brandColor` reaches the HTML string below, and it comes from the client's
 * pasted snippet — `PweChat.init({ brandColor })` — which `embed.ts` does not
 * validate. A quote in it breaks out of the style attribute exactly as a quote
 * in a URL broke out of href. Everywhere else in the widget the colour goes
 * into a React style object, which React escapes; this is the one place it
 * does not.
 *
 * Deliberately a small allowlist — hex, the rgb/hsl function forms, and bare
 * colour keywords. A value outside it is a mistake or an attack, and either
 * way the default is the right thing to draw.
 */
const COLOR_RE =
  /^(#[0-9a-f]{3,8}|[a-z]{3,20}|(?:rgb|hsl)a?\([0-9a-z\s.,%/-]{1,64}\))$/i;

export function safeColor(color: string): string {
  const trimmed = (color ?? '').trim();
  return COLOR_RE.test(trimmed) ? trimmed : DEFAULT_BRAND_COLOR;
}

/**
 * A markdown link `[label](https://…)` OR a bare `https://…` in ESCAPED text,
 * in ONE alternation so a single left-to-right pass sees each URL once.
 *
 * Two passes — links, then bare URLs — would find the `href="https://…"` the
 * first pass had just written and wrap an anchor around an anchor. One
 * alternation cannot: at a `[` the link branch consumes label and URL
 * together, and the bare branch only ever starts at an `h` the link branch
 * did not claim.
 *
 * The bare branch runs on escaped text, so the characters that end a URL are
 * the ENTITIES the escaper produces, not the raw characters: `&lt;`, `&gt;`,
 * `&quot;` and `&#39;` are where a URL stops, while `&amp;` is the literal
 * ampersand of a query string and stays in. Whitespace and a raw `<` (which
 * cannot occur after escaping, but costs nothing to exclude) end it too.
 */
const LINK_OR_URL_RE =
  /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/(?:(?!&(?:lt|gt|quot|#39);)[^\s<])+)/g;

/**
 * Punctuation a sentence puts AFTER a URL, which the pattern above cannot
 * tell from the URL's own last character: "see https://example.com/." or
 * "(at https://example.com/)". Trimmed off the match and left as text.
 *
 * A `)` is trimmed only when it does not close a `(` inside the URL —
 * Wikipedia-style `…/Foo_(bar)` keeps it. Repeated so "…/x)." sheds both.
 */
function trimTrailingPunctuation(url: string): string {
  let end = url.length;
  for (;;) {
    const last = url[end - 1];
    if (last === '.' || last === ',' || last === ';' || last === ':' || last === '!' || last === '?') {
      end -= 1;
      continue;
    }
    if (last === ')' || last === ']') {
      const open = last === ')' ? '(' : '[';
      const head = url.slice(0, end);
      const opens = head.split(open).length - 1;
      const closes = head.split(last).length - 1;
      if (closes > opens) {
        end -= 1;
        continue;
      }
    }
    return url.slice(0, end);
  }
}

/** Inline markdown → HTML. Every interpolation below is escaped or validated. */
export function renderInline(text: string, brandColor: string): string {
  const color = safeColor(brandColor);
  const anchor = (href: string, label: string) =>
    `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:${color};text-decoration:underline">${label}</a>`;
  return escapeHtml(text)
    .replace(
      /`([^`\n]+)`/g,
      '<code style="background:rgba(0,0,0,0.06);border-radius:4px;padding:1px 4px;font-size:0.9em">$1</code>',
    )
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(LINK_OR_URL_RE, (whole, label: string | undefined, url: string | undefined, bare: string | undefined) => {
      if (label !== undefined && url !== undefined) {
        const href = safeHref(url);
        // A link we will not vouch for stays as the (already escaped) text the
        // assistant wrote. Dropping it silently would hide what was said.
        if (!href) return whole;
        return anchor(href, label);
      }
      // A bare URL the assistant wrote out — "The homepage is at
      // https://example.com/" — is a link too (2026-09-02, on request): the
      // model is asked for markdown links, and when it writes the address
      // instead the reader still expects to tap it. Same allowlist, same
      // target and rel, and the text shown is the URL exactly as written.
      // Whatever punctuation the sentence put after it stays as text.
      const address = trimTrailingPunctuation(bare ?? '');
      const rest = (bare ?? '').slice(address.length);
      const href = safeHref(address);
      if (!href) return whole;
      return anchor(href, address) + rest;
    });
}

/**
 * Convert assistant markdown (headings, ordered/unordered lists, paragraphs,
 * bold/italic/code/links) to HTML for chat bubbles. Block-based — the bubble
 * does NOT use pre-wrap, so lists and paragraphs get real spacing instead of
 * stray blank lines.
 */
export function renderMarkdown(text: string, brandColor: string): string {
  const lines = text.split('\n');
  const html: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      html.push(`<p style="margin:6px 0">${para.join('<br/>')}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const tag = list.ordered ? 'ol' : 'ul';
      html.push(
        `<${tag} style="margin:6px 0;padding:0 0 0 18px">` +
          list.items.map((i) => `<li style="margin:2px 0">${i}</li>`).join('') +
          `</${tag}>`,
      );
      list = null;
    }
  };

  for (const line of lines) {
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (ul || ol) {
      flushPara();
      const ordered = !!ol;
      const item = renderInline((ul ?? ol)![1], brandColor);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push(item);
    } else if (heading) {
      flushList();
      flushPara();
      html.push(`<p style="margin:8px 0 2px;font-weight:600">${renderInline(heading[2], brandColor)}</p>`);
    } else if (!line.trim()) {
      flushList();
      flushPara();
    } else {
      flushList();
      para.push(renderInline(line, brandColor));
    }
  }
  flushList();
  flushPara();
  // The first/last block's 6px margins would pad the bubble unevenly — cancel
  // them at the wrapper so bubble padding stays visually symmetric.
  return `<div style="margin:-6px 0">${html.join('')}</div>`;
}

/**
 * Plain text for the screen-reader live region. The markup the renderer
 * would draw is noise when read aloud — "asterisk asterisk Pricing asterisk
 * asterisk" — so links keep their label, emphasis and code keep their text,
 * and heading/list markers go.
 */
export function stripMarkdown(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1$2')
    .replace(/^#{1,4}\s+/gm, '')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}
